import { Data, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import InstrumentSearchLookup from '../../../DataInterception/InstrumentMapping/Models/InstrumentSearchLookup';
import ManageInstrumentTypeComponentVM from '../../InstrumentTypes/Components/ManageInstrumentTypeComponentVM';
import CreateInstrumentSubtypeCommand from '../../InstrumentTypes/Models/Commands/CreateInstrumentSubtypeCommand';
import CreateInstrumentTypeCommand from '../../InstrumentTypes/Models/Commands/CreateInstrumentTypeCommand';
import UpdateInstrumentSubtypeCommand from '../../InstrumentTypes/Models/Commands/UpdateInstrumentSubtypeCommand';
import UpdateInstrumentTypeCommand from '../../InstrumentTypes/Models/Commands/UpdateInstrumentTypeCommand';
import InstrumentSubTypeLookup from '../../InstrumentTypes/Models/Lookups/InstrumentSubTypeLookup';
import InstrumentTypeLookup from '../../InstrumentTypes/Models/Lookups/InstrumentTypeLookup';
import InstrumentTypeSearchCriteria from '../../InstrumentTypes/Models/Queries/InstrumentTypeSearchCriteria';
import ManageInstrumentComponentVM from '../components/ManageInstrumentComponentVM';
import CreateInstrumentCommand from '../Models/Commands/CreateInstrumentCommand';
import UpdateInstrumentCommand from '../Models/Commands/UpdateInstrumentCommand';
import InstrumentSearchCriteria from '../Models/Queries/InstrumentSearchCriteria';

@NeoModel
export default class InstrumentsVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private navigation = AppService.get(Types.Neo.Routing.NavigationHelper),
        public instrumentApiClient = AppService.get(Types.ApiClients.InstrumentApiClient),
        public instrumentTypeApiClient = AppService.get(Types.ApiClients.InstrumentTypeApiClient),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)){
        super(taskRunner);
    }

    public instrumentSearchCriteria = new InstrumentSearchCriteria();
    public showManageInstrumentComponent : boolean = false;
    public manageInstrumentComponentVM = new ManageInstrumentComponentVM();
    public instrumentTypeSearchCriteria = new InstrumentTypeSearchCriteria();
    public manageInstrumentTypeVM = new ManageInstrumentTypeComponentVM();
    public showManageInstrumentTypesComponent: boolean = false;

    public instrumentsPageManager = new Data.PageManager(this.instrumentSearchCriteria, InstrumentSearchLookup,
        this.instrumentApiClient.getPagedInstruments, {
        pageSize: 10,
        sortBy: "description",
        sortAscending: true,
        fetchInitial: true,
        initialTaskRunner: this.taskRunner,
    });

    public instrumentTypesPageManager = new Data.PageManager(this.instrumentTypeSearchCriteria, InstrumentTypeLookup,
        this.instrumentTypeApiClient.getPagedInstrumentTypes, {
            pageSize: 10,
            sortBy: "instrumentTypeName",
            sortAscending: true,
            fetchInitial: true,
            initialTaskRunner: this.taskRunner,
            afterFetch: this.setIsExpandedProp.bind(this),
        });

    public async initialise() {
        reaction(() => this.instrumentSearchCriteria.searchCriteria, () => this.instrumentsPageManager.refreshData(), {fireImmediately: false, delay: 650 });
    }

    public setIsExpandedProp(data: InstrumentTypeLookup[]) {
        data.forEach(instrumentType => {
            instrumentType.isExpanded = false;
        })
    }

    public async displayManageInstrumentComponent() {
        this.showManageInstrumentComponent = true;
    }

    public cancelInstrument() {
        this.manageInstrumentComponentVM.managedInstrument = new InstrumentSearchLookup();
        this.showManageInstrumentComponent = false;
    }

    public async cancelInstrumentType() {
        this.manageInstrumentTypeVM.managedInstrumentSubtype = new InstrumentSubTypeLookup();
        this.manageInstrumentTypeVM.managedInstrumentType = new InstrumentTypeLookup();
        this.resetIndicators();
        await this.instrumentTypesPageManager.refreshData();
    }

    public async clearSearchInstrument() {
        this.instrumentSearchCriteria.meta.searchCriteria.value = "";
        await this.instrumentsPageManager.refreshData();
    }

    public async clearSearchInstrumentType() {
        this.instrumentTypeSearchCriteria.meta.searchCriteria.value = "";
        await this.instrumentTypesPageManager.refreshData();
    }

    public async editInstrument(staticInstrumentsLookup: InstrumentSearchLookup) {
        this.manageInstrumentComponentVM.managedInstrument.mapFrom(staticInstrumentsLookup).toJSObject();
        await this.manageInstrumentComponentVM.initialise();
        this.showManageInstrumentComponent = true;
    }

    public async resetIndicators() {
        this.showManageInstrumentTypesComponent = false;
        this.manageInstrumentTypeVM.instrumentTypeName = "";
    }

    public async addNewInstrumentType() {
        this.showManageInstrumentTypesComponent = true;
        await this.manageInstrumentTypeVM.initialise();
        this.manageInstrumentTypeVM.managedInstrumentType = new InstrumentTypeLookup();
    }

    public async addNewInstrumentSubtype(instrumentTypeName: string, instrumentTypeID: number) {
        await this.manageInstrumentTypeVM.initialise();
        this.manageInstrumentTypeVM.managedInstrumentSubtype = new InstrumentSubTypeLookup();
        this.manageInstrumentTypeVM.managedInstrumentSubtype.staticInstrumentTypeId = instrumentTypeID;
        this.manageInstrumentTypeVM.instrumentTypeName = instrumentTypeName;
        this.showManageInstrumentTypesComponent = true;        
    }

    public async editInstrumentType(instrumentType: InstrumentTypeLookup) {
        this.manageInstrumentTypeVM.managedInstrumentType.mapFrom(instrumentType).toJSObject();
        this.showManageInstrumentTypesComponent = true;
        await this.manageInstrumentTypeVM.initialise();
    }

    public async editInstrumentSubtype(instrumentTypeName: string, instrumentSubtype: InstrumentSubTypeLookup) {
        this.manageInstrumentTypeVM.managedInstrumentSubtype.mapFrom(instrumentSubtype).toJSObject();
        this.manageInstrumentTypeVM.instrumentTypeName = instrumentTypeName;
        this.showManageInstrumentTypesComponent = true;
        await this.manageInstrumentTypeVM.initialise();
    }

    public async saveInstrumentType() {
        this.taskRunner.run(async () => {
            var response = this.manageInstrumentTypeVM.managedInstrumentType.staticInstrumentTypeID > 0
            ? await this.instrumentTypeApiClient.updateInstrumentType((this.mapInstrumentTypeToUpdateCommand()!.toJSObject()))
            : await this.instrumentTypeApiClient.addNewInstrumentType((this.mapInstrumentTypeToCreateCommand()!.toJSObject()));

        if (response.data.success) {
            await this.instrumentTypesPageManager.refreshData();
            NotifyUtils.addSuccess(TextConstants.Titles.Success, "Instrument type has been successfully saved.", NotificationDuration.Standard);
        }
        else {
            NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
        }
        this.resetIndicators();
        })
    }

    public async saveInstrumentSubtype() {
       
        this.taskRunner.run(async () => {
            var response = this.manageInstrumentTypeVM.managedInstrumentSubtype.staticInstrumentSubTypeId > 0
            ? await this.instrumentTypeApiClient.updateInstrumentSubtype((this.mapInstrumentSubtypeToUpdateCommand()!.toJSObject()))
            : await this.instrumentTypeApiClient.addNewInstrumentSubtype((this.mapInstrumentSubtypeToCreateCommand()!.toJSObject()));

            if (response.data.success) {
                await this.instrumentTypesPageManager.refreshData();
                NotifyUtils.addSuccess(TextConstants.Titles.Success, "Instrument sub type has been successfully saved.", NotificationDuration.Standard);
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
            }
            this.resetIndicators();
        })
    }

    public async saveInstrument() {
        this.taskRunner.run(async () => {
            var response = this.manageInstrumentComponentVM.managedInstrument.instrumentID > 0
                ? await this.instrumentApiClient.updateInstrument((this.mapInstrumentToUpdateCommand()!.toJSObject()))
                : await this.instrumentApiClient.createInstrument((this.mapInstrumentToCreateCommand()!.toJSObject()));

            if (response.data.success) {
                await this.instrumentsPageManager.refreshData();
                NotifyUtils.addSuccess(TextConstants.Titles.Success, "The Instrument has been successfully saved.", NotificationDuration.Standard);
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
            }
            this.manageInstrumentComponentVM.managedInstrument = new InstrumentSearchLookup();
            this.showManageInstrumentComponent = false;
        });
    }

    private mapInstrumentTypeToCreateCommand() {
        var command = new CreateInstrumentTypeCommand().mapFrom(this.manageInstrumentTypeVM.managedInstrumentType);
        return command;
    }

    private mapInstrumentSubtypeToCreateCommand() {
        var command = new CreateInstrumentSubtypeCommand().mapFrom(this.manageInstrumentTypeVM.managedInstrumentSubtype);
        command.staticInstrumentTypeID = this.manageInstrumentTypeVM.managedInstrumentSubtype.staticInstrumentTypeId;
        return command;
    }

    private mapInstrumentTypeToUpdateCommand() {
        var command = new UpdateInstrumentTypeCommand().mapFrom(this.manageInstrumentTypeVM.managedInstrumentType);
        return command;
    }

    private mapInstrumentSubtypeToUpdateCommand() {
        var command = new UpdateInstrumentSubtypeCommand().mapFrom(this.manageInstrumentTypeVM.managedInstrumentSubtype);
        return command;
    }

    private mapInstrumentToCreateCommand() {
        var command = new CreateInstrumentCommand().mapFrom(this.manageInstrumentComponentVM.managedInstrument);
        return command;
    }

    private mapInstrumentToUpdateCommand() {
        var command = new UpdateInstrumentCommand().mapFrom(this.manageInstrumentComponentVM.managedInstrument);
        return command;
    }
}